import React from 'react';
import DefaultLayout from '../../layouts/DefaultLayout';
import TemplateColumn from '../../layouts/TemplateColumn';
import { PageHeader, PageSection, SecondaryCallout, Image, RelatedContent, PageSubSection, TextBlock } from '../../components';
import { StaticImage } from 'gatsby-plugin-image';
import { jobCodes } from '../../util/data/jobCodes';
import styled from 'styled-components';
import MOACard from '../../components/MOACard';
import { between } from 'polished';
// SEO INFORMATION
const pageMeta = {
    title: `Mechanism of Action for KEYTRUDA® (pembrolizumab) and KEYTRUDA QLEX™ (pembrolizumab and berahyaluronidase alfa-pmph)`,
    keywords: `keytruda mechanism of action, pembrolizumab mechanism of action`,
    description: `Learn more about the mechanisms of action for KEYTRUDA® (pembrolizumab) and KEYTRUDA QLEX™ (pembrolizumab and berahyaluronidase alfa-pmph).`,
    schemaJsonLD: [
        `{"@context":"https://schema.org/","url":"https://www.keytrudahcp.com/resources/mechanism-of-action/","@type":"MedicalAudience","audienceType":"Health Care Professionals","geographicArea":"This site is intended for health care professionals of the United States, its territories, and Puerto Rico."}`,
        `{"@context":"https://schema.org/","url":"https://www.keytrudahcp.com/resources/mechanism-of-action/","@type":"Substance","name":"KEYTRUDA®","alternateName":"pembrolizumab","description":"KEYTRUDA binds to the PD⁠-⁠1 receptor, blocking both immune-suppressing ligands, PD‑L1 and PD‑L2, from interacting with PD⁠-⁠1 to help restore T-cell response and immune response","activeIngredient":"pembrolizumab","recognizingAuthority":"Merck"}`
    ]
}

const jobCode = jobCodes[31].jobCode;

const keytrudaCardMoa = [
    { 
      image: () => (
        <img src="/static/images/mechanism-of-action/Normal_Immune.png" alt="Pembrolizumab: PD-1 Receptor Blockade Normal Immune Response" width="100%" height="100%"  />
      ), 
      caption: 'Normal immune response',
      description: 'When functioning properly, T cells are activated and can attack tumor cells.'  
    },
    { 
     image: () => (
        <img src="/static/images/mechanism-of-action/TmrEvasion_TCDeactivation.png" alt="Pembrolizumab: PD-1 Receptor Blockade Tumor Evasion and T-cell Deactivation" width="100%" height="100%" />
      ), 
      caption: 'Tumor evasion and T-cell deactivation',
      description: `Some tumors can evade the immune system through the PD⁠-⁠1 pathway. The PD‑L1 and PD‑L2 ligands on tumors can bind with PD⁠-⁠1 receptors on T cells to inactivate the T cells.`  
    },
    {
      image: () => (
        <img src="/static/images/mechanism-of-action/T_Cell_Reactivation_RGB.png" alt="Pembrolizumab: PD-1 Receptor Blockade T-cell Reactivation" width="100%" height="100%" />
      ), 
      caption: 'T-cell reactivation with Pembrolizumab',
      description: 'Pembrolizumab binds to the PD⁠-⁠1 receptor and blocks its interaction with PD‑L1 and PD‑L2, which helps restore the immune response. While having an effect on the tumor, this could also affect normal healthy cells.'  
    }
]

const keytrudaQlexCardMoa = [
    { image: () => (
        <img src="/static/images/mechanism-of-action/Hyaluronan.png" alt="Berahyaluronidase Alfa Mechanism of Action in KEYTRUDA QLEX™ (pembrolizumab and berahyaluronidase alfa-pmph): Hyaluronan in the Subcutaneous Space" width="100%" height="100%" />
      ), 
      caption: 'Hyaluronan in the subcutaneous space',
      description: 'Hyaluronan is a polysaccharide found in the extracellular matrix of the subcutaneous tissue. Unlike the stable structural components of the interstitial matrix, hyaluronan has a half-life of approximately 0.5 days.'  
    },
    { image: () => (
        <img src="/static/images/mechanism-of-action/Degradation_of_hyaluronan.png" alt="Berahyaluronidase Alfa Mechanism of Action in KEYTRUDA QLEX™ (pembrolizumab and berahyaluronidase alfa-pmph): Degradation of Hyaluronan by berahyaluronidase alfa" width="100%" height="100%" />
      ), 
      caption: 'Degradation of hyaluronan by berahyaluronidase alfa',
      description: 'Hyaluronidase increases permeability of the subcutaneous tissue by depolymerizing hyaluronan. In the doses administered, the effects of berahyaluronidase alfa are reversible and permeability of the subcutaneous tissue is restored within 24 to 48 hours.'  
    },
    { 
      image: () => (
        <img src="/static/images/mechanism-of-action/Increased_Drug.png" alt="Berahyaluronidase Alfa Mechanism of Action in KEYTRUDA QLEX™ (pembrolizumab and berahyaluronidase alfa-pmph): Increased Drug Permeation" width="100%" height="100%" />
      ), 
      caption: 'Increased drug permeation',
      description: 'Berahyaluronidase alfa facilitates delivery of the increased volume of pembrolizumab that is coadministered subcutaneously.'  
    }
]

const relatedContent = [
    {
        section: 'Biomarker Testing',
        link: {
            url: '/biomarker-testing/pd-l1/',
            label: 'PD&#8288;-&#8288;L1 Testing & Scoring'
        }
    },
    {
        section: 'Safety',
        link: {
            url: '/safety/adverse-reactions/',
            label: 'Selected Adverse Reactions'
        }
    },
    {
        section: 'Dosing & Preparation',
        link: {
            url: '/dosing/options/',
            label: 'Dosing'
        }
    }
]

const KeytrudaMoaContent = `<p>Pembrolizumab binds to the PD-1 receptor, blocking both immune-suppressing ligands, PD-L1 and PD-L2, from interacting with PD-1 to help restore T-cell response and immune response.</p>
                <p>Restoring active T-cell response could affect both normal healthy cells and tumor cells.</p>`;

const ImageAlignment = styled.div`
    // .gatsby-image-wrapper-constrained {
    //     vertical-align: bottom;
    // }
    >div>div>div>div{
     vertical-align: bottom;
    }
`;

const PageSectionWrapper = styled.div`
[class*="PageSubSection__Container"]{
&:not(:first-child){
    margin-top: ${between('60px', '80px', '320px', '1032px')};   
}
    hr {
        margin-bottom: 20px;
    }
}
`;

const Page = ({ location }) => {

    return (
        <DefaultLayout jobCode={jobCode} pageMeta={pageMeta} location={location}>
            <div data-template-name="template-c">
                {/* Begin Intro Column */}
                <TemplateColumn id="template-c-intro">
                    <PageHeader title="Mechanism of Action"  batch2 brand="qlex" />
                </TemplateColumn>
                {/* End Intro Column */}

                {/* Begin Main Column */}
                <TemplateColumn id="template-c-main">
                    <PageSectionWrapper>
                    <PageSection>
                        <PageSubSection title="Pembrolizumab: PD-1 Receptor Blockade in KEYTRUDA and KEYTRUDA QLEX™ (pembrolizumab and berahyaluronidase alfa-pmph) Injection 165 mg + 2,000 units/mL">
                            <MOACard                                
                                description={KeytrudaMoaContent}
                                def="PD⁠-⁠1 = programmed death receptor-1; PD⁠-⁠L1 = programmed death ligand 1; PD⁠-⁠L2 = programmed death ligand 2."
                                cards={keytrudaCardMoa}
                            />
                        </PageSubSection>

                        <PageSubSection title="Berahyaluronidase Alfa Mechanism of Action in KEYTRUDA QLEX" isQsub={true}>
                            <MOACard                               
                                description="Berahyaluronidase alfa, an endoglycosidase, is a variant of human hyaluronidase PH20 that temporarily and locally breaks down hyaluronan."
                                cards={keytrudaQlexCardMoa}
                                type="qlex" 
                                deskImgHeight='323px'
                                mobImgHeight='280px'
                            />
                        </PageSubSection>                                              
                    </PageSection>
                    </PageSectionWrapper>
                    <PageSection bgColor="keytrudaGreen">
                        <RelatedContent items={relatedContent} />
                    </PageSection>
                </TemplateColumn>
                {/* End Main Column */}
            </div>
        </DefaultLayout>
    );
};

export default Page;